import type { Option } from '@pbk-web/types';
import { convertOfficeToPdf, PbkNutrientSupportedMimeTypes } from '@pbk-web/ui';
import { v4 as uuidv4 } from 'uuid';

import type { FileInfo } from '../components/shared/file-upload/FileInfo';
import DataExportService from '../services/DataExportService';
import FileService from '../services/FileService';

const units = ['b', 'kb', 'mb', 'gb'];

export const FILE_EXTENSIONS = {
  JPG: 'jpg',
  PNG: 'png',
  GIF: 'gif',
  PDF: 'pdf',
  DOC: 'doc',
  XLS: 'xls',
  DOCX: 'docx',
  XLSX: 'xlsx',
  MP4: 'mp4',
} as const;

export type FILE_EXTENSION = (typeof FILE_EXTENSIONS)[keyof typeof FILE_EXTENSIONS];

export const MIME_TYPES = {
  JPEG: 'image/jpeg',
  PNG: 'image/png',
  GIF: 'image/gif',
  PDF: 'application/pdf',
  DOC: 'application/msword',
  XLS: 'application/vnd.ms-excel',
  DOCX: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  MP4: 'video/mp4',
} as const;

export type MIME_TYPE = (typeof MIME_TYPES)[keyof typeof MIME_TYPES];

export const mimeToExtensionMap: Record<MIME_TYPE, FILE_EXTENSION> = {
  [MIME_TYPES.JPEG]: FILE_EXTENSIONS.JPG,
  [MIME_TYPES.PNG]: FILE_EXTENSIONS.PNG,
  [MIME_TYPES.GIF]: FILE_EXTENSIONS.GIF,
  [MIME_TYPES.PDF]: FILE_EXTENSIONS.PDF,
  [MIME_TYPES.DOC]: FILE_EXTENSIONS.DOC,
  [MIME_TYPES.XLS]: FILE_EXTENSIONS.XLS,
  [MIME_TYPES.DOCX]: FILE_EXTENSIONS.DOCX,
  [MIME_TYPES.XLSX]: FILE_EXTENSIONS.XLSX,
  [MIME_TYPES.MP4]: FILE_EXTENSIONS.MP4,
};

export const MimeTypeOptions: Option<string, string>[] = Object.entries(mimeToExtensionMap).map(([mimeType, extension]) => ({
  id: mimeType,
  text: extension,
  value: mimeType,
}));

export class FileUtils {
  // https://stackoverflow.com/a/18650828
  static formatSize(bytes: number): string {
    if (bytes === 0) {
      return '0 b';
    }

    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(1)) + ' ' + units[i];
  }

  static removeDuplicates(files: FileInfo[]): FileInfo[] {
    return files.filter((file, index, arr) => arr.findIndex((f) => f.id === file.id) === index);
  }

  static mapFileInfo(files: File[]): FileInfo[] {
    return files.map((file) => {
      return this.toFileInfo(file);
    });
  }

  static toFileInfo(file: File): FileInfo {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return { id: (file as any).id || uuidv4(), name: file.name, type: file.type, size: file.size, mimeType: file.type, file };
  }

  static download(url: string, fileName = ''): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
  }

  static downloadFile({ id, name, addExtension }: { id: string; name: string; addExtension?: boolean }) {
    FileService.getFile(id).then((fileBlob) => {
      const url = URL.createObjectURL(fileBlob);
      const extension = this.getFileExtensionFromMimeType(fileBlob.type);
      const fileName = addExtension && !name.endsWith(extension) ? `${name}.${extension}` : name;
      this.download(url, fileName);
    });
  }

  static downloadAttachment({ id, name, addExtension }: { id: string; name: string; addExtension?: boolean }) {
    this.getAttachmentUrl(id, name, addExtension).then((result) => {
      if (!result) return;
      const { url, fileName } = result;
      this.download(url, fileName);
    });
  }

  static getAttachmentUrl(
    attachmentId: string,
    name: string,
    addExtension?: boolean,
  ): Promise<{ url: string; fileName: string; fileBlob: Blob } | null> {
    return DataExportService.downloadAttachment(attachmentId).then((fileBlob) => {
      if (fileBlob.size === 0) return null;
      const extension = this.getFileExtensionFromMimeType(fileBlob.type);
      const fileName = addExtension && !name.endsWith(extension) ? `${name}.${extension}` : name;
      const url = URL.createObjectURL(fileBlob);
      return { url, fileName, fileBlob };
    });
  }

  static getFileUrl(fileId: string): Promise<string | null> {
    return FileService.getFile(fileId).then((fileBlob) => {
      if (fileBlob.size === 0) return null;

      const url = URL.createObjectURL(fileBlob);
      return url;
    });
  }

  /**
   * Resolve a file as a PDF object URL, suitable for the Nutrient comparison
   * viewer (which only accepts PDF). PDFs pass through unchanged; supported
   * Office documents are converted to PDF in-browser. Returns null for empty
   * files; rejects for unsupported types.
   */
  static async getComparablePdfUrl(fileId: string): Promise<string | null> {
    const fileBlob = await FileService.getFile(fileId);
    if (fileBlob.size === 0) return null;

    if (fileBlob.type === MIME_TYPES.PDF) {
      return URL.createObjectURL(fileBlob);
    }

    if (!PbkNutrientSupportedMimeTypes.includes(fileBlob.type)) {
      throw new Error(`Unsupported file type for comparison: ${fileBlob.type || 'unknown'}`);
    }

    const pdfBuffer = await convertOfficeToPdf(await fileBlob.arrayBuffer());
    return URL.createObjectURL(new Blob([pdfBuffer], { type: MIME_TYPES.PDF }));
  }

  static getFileNameFromPath(filePath: string) {
    const parts = filePath.split('/');
    return parts[parts.length - 1];
  }

  static getFileExtensionFromMimeType(mimeType: string): FILE_EXTENSION | string {
    if (!mimeType) {
      return '';
    }

    const lowerCaseMimeType = mimeType.toLowerCase() as MIME_TYPE;

    if (mimeToExtensionMap[lowerCaseMimeType]) {
      return mimeToExtensionMap[lowerCaseMimeType];
    } else {
      // If not found in the mapping, attempt to extract it
      // from the MIME type itself (not recommended for all types)
      const ext = mimeType.split('/').pop();
      return ext ? ext : '';
    }
  }

  static removeFileExtension(filename: string): string {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1) return filename;
    return filename.substring(0, lastDotIndex);
  }

  static getFileExtension(filename: string): string {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1) return '';
    return filename.substring(lastDotIndex + 1).toLowerCase();
  }
}
